import { PaymentTypeOptions } from "Types/state";
import { Store } from "src/store";
import signIn from "src/sdk/signIn";
import { OnlineShopper, StartOptions } from "src/types/window";
import { logError, logInfo } from "src/utils/logger";
import { addGarageReporter } from "src/store/dynamicGarageReporters";
import initialize from "src/sdk/initialize";
import toggleOS from "src/sdk/toggleOS";
import { ApiVehicle } from "Types/api/Vehicle";
import { Environment } from "Types/Environment";
import openTradeIn from "src/sdk/openTradeIn";

let store: Store;
let bootstrapped: boolean = false;

const queue: Array<() => void> = [];

const SDK: OnlineShopper = {
    APP_VERSION: process.env.APP_VERSION!,
    BUILD_NUMBER: process.env.BUILD_NUMBER!,

    // Initialize the app. Apart of the public interface due to how our test page works, but should not be used by 3rd parties
    async bootstrap(accountSlug: string, env: Environment, srpLink: string) {
        bootstrapped = false; // Reset to false so that our test page can call bootstrap more than once (to change accounts)

        store = await initialize(accountSlug, env, srpLink);

        bootstrapped = true;

        const event = new CustomEvent("OnlineShopperReady");
        window.dispatchEvent(event);

        // Handle any start() or toggleOS() calls that were queued while we were bootstrapping
        while (queue.length > 0) {
            const command = queue.shift();
            command && command();
        }
    },

    // Previously relied on loader script to call this to start the app
    // Now only used to handle third party callbacks
    start(
        ...args:
            | [any, any]
            | [(vehicles: ApiVehicle[], os: any) => void, () => void]
            | [any, any, any, any]
            | [string, StartOptions, (vehicles: ApiVehicle[], os: any) => void, any]
    ) {
        const DEFAULT_VIN_SELECTOR = "data-vin";
        const DEFAULT_ORIGINAL_TEXT_SELECTOR = "data-original-text";
        const ONSHOP_DOMAIN_TEXT = "onlineshopper";

        let onGarageUpdate: any;
        let startedCallback: any;

        if (!args[0] && ONSHOP_DOMAIN_TEXT.indexOf(window.location.href) === -1) {
            // if no CTA callback is provided and this isn't our inventory test page
            logInfo(`CTACallback was not provided for domain ${window.location.href}`);
        }

        if (args.length === 4) {
            logInfo(`${window.location.href} implemented OS without the loader`);
        }

        if (args.length === 2) {
            if (!args[0]) {
                defaultInit();
                args[0] = defaultUpdateCTACallback;
            }
            onGarageUpdate = args[0];
            startedCallback = args[1];
        } else {
            // direct call from (presumably) test page
            onGarageUpdate = args[2];
            startedCallback = args[3];
        }

        function defaultUpdateCTACallback(garage: any) {
            const allVinNodes = document.querySelectorAll(`[${DEFAULT_VIN_SELECTOR}]`) as NodeListOf<HTMLElement>;

            Array.from(allVinNodes).forEach((element: HTMLElement) => {
                const original = element.getAttribute(DEFAULT_ORIGINAL_TEXT_SELECTOR);
                if (!original) {
                    element.setAttribute(DEFAULT_ORIGINAL_TEXT_SELECTOR, element.innerText);
                } else {
                    element.innerText = original;
                }
            });

            garage.forEach((element: any) => {
                const button = document.querySelector(`[${DEFAULT_VIN_SELECTOR}='${element.vin}']`);

                if (button) {
                    button.innerHTML = "In your garage!";
                }
            });
        }

        function defaultInit() {
            const allVinNodes = document.querySelectorAll(`[${DEFAULT_VIN_SELECTOR}]`) as NodeListOf<HTMLElement>;
            Array.from(allVinNodes).forEach((element: HTMLElement) => {
                const original = element.getAttribute(DEFAULT_ORIGINAL_TEXT_SELECTOR);

                if (!original) {
                    element.setAttribute(DEFAULT_ORIGINAL_TEXT_SELECTOR, element.innerText);
                }

                element.addEventListener(
                    "click",
                    () => {
                        window.OnlineShopper.toggleOS(element.getAttribute(DEFAULT_VIN_SELECTOR) ?? undefined);
                    },
                    false
                );
            });
        }

        if (bootstrapped) {
            if (onGarageUpdate && typeof onGarageUpdate === "function") {
                addGarageReporter(onGarageUpdate);
            }

            if (startedCallback && typeof startedCallback === "function") {
                startedCallback();
            }
        } else {
            queue.unshift(() => window.OnlineShopper.start(onGarageUpdate, startedCallback));
        }
    },

    openTradeIn(vin: string) {
        if (bootstrapped) {
            openTradeIn(store, vin);
        } else {
            queue.push(async () => window.OnlineShopper.openTradeIn(vin));
        }
    },

    toggleOS(vin?: string, paymentType?: PaymentTypeOptions) {
        if (bootstrapped) {
            const state = store?.getState();

            // If we're already open, don't do anything
            if (state?.onlineShopper?.isAppOpen) {
                return;
            }

            toggleOS(store, vin, paymentType).catch((e) => logError(e));
        } else {
            queue.push(async () => window.OnlineShopper.toggleOS(vin, paymentType));
        }
    },

    signIn() {
        if (bootstrapped) {
            signIn(store);
        } else {
            queue.push(async () => window.OnlineShopper.signIn());
        }
    },

    getDealUUID() {
        return store?.getState()?.deal?.uuid || null;
    },
};

export default SDK;
